<template>
  <v-container class="school-exit-page py-7">
    <div class="title-container">
      <div class="title-block">
        <span class="page-title">Painel de saída</span>
        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs" />
      </div>
      <div class="button-group">
        <v-btn outlined color="primary" :loading="loading" @click="load">
          <v-icon left>mdi-refresh</v-icon>
          Atualizar
        </v-btn>
        <v-btn color="primary" elevation="0" @click="manualDialog = true">
          <v-icon left>mdi-account-plus-outline</v-icon>
          Solicitação manual
        </v-btn>
      </div>
    </div>

    <div class="exit-summary">
      <v-card v-for="summary in summaries" :key="summary.status" class="summary-card">
        <div class="summary-icon" :class="`summary-icon--${summary.color}`">
          <v-icon>{{ summary.icon }}</v-icon>
        </div>
        <div>
          <span>{{ summary.label }}</span>
          <strong>{{ summary.count }}</strong>
        </div>
      </v-card>
      <v-card class="summary-card connection-card">
        <span class="connection-dot" :class="{ offline: !connected }" />
        <div>
          <span>{{ connected ? 'Atualização ativa' : 'Sem comunicação' }}</span>
          <small>{{ lastUpdatedLabel }}</small>
        </div>
      </v-card>
    </div>

    <v-card class="filter-card exit-filter">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="7">
            <v-text-field
              v-model="search"
              dense
              outlined
              clearable
              hide-details
              label="Buscar aluno"
              placeholder="Nome, CPF ou matrícula"
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="load"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="view" dense outlined hide-details :items="views" label="Visualização" />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn block color="primary" elevation="0" @click="load">Filtrar</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="exit-table-card">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="30"
        hide-default-footer
        class="app-table exit-table"
        no-data-text="Nenhum aluno aguardando saída"
        loading-text="Atualizando fila de saída..."
      >
        <template v-slot:[`item.student`]="{ item }">
          <div class="student-cell">
            <v-avatar size="44" color="#eaf2ff">
              <v-img v-if="item.student && item.student.document_url" :src="item.student.document_url" />
              <span v-else>{{ initials(item.student && item.student.name) }}</span>
            </v-avatar>
            <div>
              <strong>{{ item.student ? item.student.name : 'Aluno' }}</strong>
              <small>{{ className(item) }}</small>
            </div>
          </div>
        </template>

        <template v-slot:[`item.source`]="{ item }">
          <span>{{ sourceLabel(item.source) }}</span>
        </template>

        <template v-slot:[`item.requested_at`]="{ item }">
          <strong>{{ $moment(item.requested_at).format('HH:mm') }}</strong>
          <small class="time-wait">{{ waitingTime(item.requested_at) }}</small>
        </template>

        <template v-slot:[`item.access_point`]="{ item }">
          <div v-if="accessPoint(item)" class="access-point-cell">
            <strong><v-icon small>mdi-map-marker-outline</v-icon>{{ accessPoint(item).name }}</strong>
            <small v-if="item.location_distance_meters !== null && item.location_distance_meters !== undefined">
              aproximadamente {{ formatDistance(item.location_distance_meters) }}
            </small>
          </div>
          <span v-else class="muted-value">Não exigida</span>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip small label :class="`status-chip status-chip--${item.status}`">
            <span class="status-dot" />
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="row-actions">
            <v-tooltip bottom v-if="item.status === 'requested'">
              <template #activator="{ on }">
                <v-btn icon v-on="on" :loading="actingId === item.id" @click="transition(item, 'calling')">
                  <v-icon>mdi-bullhorn-outline</v-icon>
                </v-btn>
              </template>
              <span>Chamar aluno</span>
            </v-tooltip>
            <v-tooltip bottom v-if="['requested', 'calling'].includes(item.status)">
              <template #activator="{ on }">
                <v-btn icon color="success" v-on="on" :loading="actingId === item.id" @click="transition(item, 'released')">
                  <v-icon>mdi-lock-open-check-outline</v-icon>
                </v-btn>
              </template>
              <span>Liberar saída</span>
            </v-tooltip>
            <v-tooltip bottom v-if="['requested', 'calling', 'released'].includes(item.status)">
              <template #activator="{ on }">
                <v-btn icon color="error" v-on="on" :loading="actingId === item.id" @click="transition(item, 'cancelled')">
                  <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>Cancelar solicitação</span>
            </v-tooltip>
            <v-tooltip bottom v-if="['cancelled', 'expired'].includes(item.status)">
              <template #activator="{ on }">
                <v-btn icon color="primary" v-on="on" :loading="actingId === item.id" @click="transition(item, 'calling')">
                  <v-icon>mdi-backup-restore</v-icon>
                </v-btn>
              </template>
              <span>Reabrir solicitação</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="manualDialog" max-width="620" persistent>
      <v-card>
        <v-card-title>Solicitação manual</v-card-title>
        <v-card-text>
          <p class="dialog-description">Use esta opção para contingências ou atendimento presencial na portaria.</p>
          <student-autocomplete
            v-model="manual.studentId"
            label="Aluno"
            @selected="manual.student = $event"
          />
          <v-checkbox
            v-model="manual.force"
            class="mt-4"
            label="Ignorar bloqueio do CHEGUEI para esta solicitação"
            hide-details
          />
          <v-alert v-if="manual.force" dense outlined type="warning" class="mt-3">
            A exceção ficará registrada com o usuário, data, hora e motivo informado.
          </v-alert>
          <v-textarea
            v-if="manual.force"
            v-model="manual.reason"
            dense
            outlined
            rows="2"
            label="Motivo da exceção"
            :rules="[$rules.required]"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeManual">Cancelar</v-btn>
          <v-btn color="primary" elevation="0" :loading="manual.loading" @click="createManual">Adicionar à fila</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import StudentAutocomplete from "@/layouts/components/StudentAutocomplete.vue";

export default {
  name: "SchoolExitPanel",
  components: { StudentAutocomplete },

  data: () => ({
    loading: false,
    connected: true,
    actingId: null,
    lastUpdatedAt: null,
    refreshTimer: null,
    search: "",
    view: "active",
    items: [],
    manualDialog: false,
    manual: { studentId: null, student: null, force: false, reason: "", loading: false },
    views: [
      { text: "Fila ativa", value: "active" },
      { text: "Concluídos hoje", value: "completed" },
      { text: "Cancelados", value: "cancelled" },
    ],
    headers: [
      { text: "Aluno", value: "student", sortable: false },
      { text: "Solicitado por", value: "source", sortable: false },
      { text: "Horário", value: "requested_at", sortable: false },
      { text: "Portaria", value: "access_point", sortable: false },
      { text: "Situação", value: "status", sortable: false },
      { text: "Ações", value: "actions", sortable: false, align: "end" },
    ],
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Painel de saída", disabled: true },
    ],
  }),

  computed: {
    statusFilter() {
      if (this.view === "completed") return "exited";
      if (this.view === "cancelled") return "cancelled,expired";
      return "requested,calling,released";
    },
    summaries() {
      const count = (status) => this.items.filter((item) => item.status === status).length;
      return [
        { status: "calling", label: "Chamando", count: count("calling") + count("requested"), icon: "mdi-bullhorn-outline", color: "warning" },
        { status: "released", label: "Liberados", count: count("released"), icon: "mdi-lock-open-check-outline", color: "success" },
        { status: "exited", label: "Saídas concluídas", count: count("exited"), icon: "mdi-exit-run", color: "primary" },
      ];
    },
    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return "Aguardando primeira atualização";
      return `Atualizado às ${this.$moment(this.lastUpdatedAt).format("HH:mm:ss")}`;
    },
  },

  created() {
    this.load();
    this.refreshTimer = window.setInterval(this.load, 8000);
  },

  beforeDestroy() {
    if (this.refreshTimer) window.clearInterval(this.refreshTimer);
  },

  methods: {
    async load() {
      if (this.loading) return;
      this.loading = true;
      try {
        const result = await axios.get("/api/v1/school-exit/requests", {
          params: { status: this.statusFilter, search: this.search || undefined, limit: 100 },
        });
        this.items = result.data.content.data || [];
        this.connected = true;
        this.lastUpdatedAt = new Date();
      } catch (error) {
        this.connected = false;
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.loading = false;
      }
    },
    async transition(item, status) {
      const labels = { calling: "chamar", released: "liberar", cancelled: "cancelar" };
      const confirmed = await this.$dialog.confirm({
        title: "Confirmar operação",
        text: `Deseja ${labels[status] || "atualizar"} ${item.student ? item.student.name : "este aluno"}?`,
        actions: { false: "Voltar", true: "Confirmar" },
      });
      if (!confirmed) return;
      this.actingId = item.id;
      try {
        await axios.patch(`/api/v1/school-exit/requests/${item.id}`, { status, source: "backoffice" });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.actingId = null;
      }
    },
    async createManual() {
      if (!this.manual.studentId) {
        this.$dialog.notify.warning("Selecione um aluno.", { position: "top-right", timeout: 4000 });
        return;
      }
      if (this.manual.force && !this.manual.reason.trim()) {
        this.$dialog.notify.warning("Informe o motivo da exceção.", { position: "top-right", timeout: 4000 });
        return;
      }
      this.manual.loading = true;
      try {
        await axios.post("/api/v1/school-exit/requests", {
          student_id: this.manual.studentId,
          source: "backoffice",
          force: this.manual.force,
          reason: this.manual.reason,
        });
        this.closeManual();
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.manual.loading = false;
      }
    },
    closeManual() {
      this.manualDialog = false;
      this.manual = { studentId: null, student: null, force: false, reason: "", loading: false };
    },
    initials(name) {
      return String(name || "A").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
    },
    className(item) {
      const student = item.student || {};
      return student.class && student.class.name ? `Turma ${student.class.name}` : "Sem turma ativa";
    },
    accessPoint(item) {
      return item.accessPoint || item.access_point || null;
    },
    formatDistance(value) {
      const meters = Number(value || 0);
      if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
      return `${Math.round(meters)} m`;
    },
    sourceLabel(source) {
      return { answerable: "Responsável", transporter: "Transporte escolar", backoffice: "Portaria", monitor: "Monitor" }[source] || "Escola";
    },
    statusLabel(status) {
      return { requested: "Solicitado", calling: "Chamando", released: "Liberado", exited: "Saiu", cancelled: "Cancelado", expired: "Expirado" }[status] || status;
    },
    waitingTime(date) {
      const minutes = Math.max(this.$moment().diff(this.$moment(date), "minutes"), 0);
      return minutes < 1 ? "agora" : `há ${minutes} min`;
    },
  },
};
</script>

<style lang="scss" scoped>
.school-exit-page { padding-bottom: 36px !important; }
.exit-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin: 18px 0; }
.summary-card { align-items: center; border: 1px solid #e5eaf1 !important; border-radius: 8px !important; box-shadow: none !important; display: flex; gap: 12px; min-height: 92px; padding: 16px; }
.summary-card span, .summary-card small { color: #64748b; display: block; font-size: 12px; font-weight: 700; }
.summary-card strong { color: #0f172a; display: block; font-size: 26px; line-height: 30px; }
.summary-icon { align-items: center; background: #eaf2ff; border-radius: 8px; display: flex; height: 42px; justify-content: center; width: 42px; }
.summary-icon--warning { background: #fff7e6; color: #b76e00; }
.summary-icon--success { background: #e8f7f1; color: #16855b; }
.summary-icon--primary { background: #eaf2ff; color: #0554b5; }
.connection-card { align-items: center; }
.connection-dot { background: #20a475; border-radius: 50%; height: 10px; width: 10px; }
.connection-dot.offline { background: #d14343; }
.exit-filter { margin-bottom: 18px; }
.exit-table-card { border: 1px solid #e5eaf1 !important; border-radius: 8px !important; box-shadow: 0 12px 34px rgba(15, 23, 42, .035) !important; overflow: hidden; }
.student-cell { align-items: center; display: flex; gap: 12px; min-width: 240px; padding: 6px 0; }
.student-cell strong, .student-cell small { display: block; }
.student-cell strong { color: #0f172a; font-size: 14px; }
.student-cell small, .time-wait { color: #64748b; font-size: 12px; }
.time-wait { display: block; font-weight: 500; }
.access-point-cell strong, .access-point-cell small { display: block; }
.access-point-cell strong { align-items: center; display: flex; gap: 4px; }
.access-point-cell small, .muted-value { color: #64748b; font-size: 12px; }
.status-chip { font-weight: 750; gap: 7px; }
.status-dot { background: currentColor; border-radius: 50%; display: inline-block; height: 7px; width: 7px; }
.status-chip--calling, .status-chip--requested { background: #fff3db !important; color: #9a5b00 !important; }
.status-chip--released { background: #e4f6ef !important; color: #137652 !important; }
.status-chip--exited { background: #eaf2ff !important; color: #0554b5 !important; }
.status-chip--cancelled, .status-chip--expired { background: #f1f4f8 !important; color: #64748b !important; }
.row-actions { align-items: center; display: flex; justify-content: flex-end; min-width: 112px; }
.dialog-description { color: #64748b; margin-bottom: 18px; }
@media (max-width: 960px) { .exit-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) { .exit-summary { grid-template-columns: 1fr; } .button-group { flex-wrap: wrap; } }
</style>
